import React, { useEffect } from 'react';
import BrandStatus from 'components/brand-status';
import BrandImageDetailCard from 'components/brand-image-detail-card';
import BrandDetailsCard from 'components/brand-details-card';
import { WorkingHoursFormApiType } from 'utils/types';
import useNewBrandContext from 'contexts/new-brand.context';
import { Input } from '@cokitchen/cokitchen-components';
import { ALL_BRAND_TYPE_OPTIONS, FC_BRAND_TYPE_OPTIONS, POLYGON_COLORS } from 'utils/constants';
import { formatTimeSelector, isFCBBrand } from 'utils/functions';
import { ReactComponent as HexagonIcon } from 'assets/icons/hexagon.svg';

type BrandTypeCheckbox = {
  label: string;
  checked: boolean;
  name: string;
};

const CreateBrandPreview = () => {
  const { createBrandForm, companyOptions, setCreateBrandForm, setDisabled } = useNewBrandContext();
  const brandImageData = {
    brandImage: createBrandForm?.images?.[0],
    brandLogo: createBrandForm?.logo
  };

  const selectedCompanies = createBrandForm.company_ids
    .map((c) => companyOptions.find((o) => o.value === c)?.text)
    .filter(Boolean);

  useEffect(() => {
    if (
      createBrandForm?.name &&
      createBrandForm?.short_text &&
      createBrandForm?.summary &&
      createBrandForm?.description &&
      createBrandForm?.images?.[0] &&
      createBrandForm?.logo
    ) {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
  }, [createBrandForm]);

  const brandTypes: BrandTypeCheckbox[] = [
    {
      label: 'Pre Order',
      checked: createBrandForm.preorder ?? false,
      name: 'preorder'
    },
    {
      label: 'Private',
      checked: createBrandForm.is_private ?? false,
      name: 'is_private'
    },

    {
      label: 'FCB',
      checked: isFCBBrand(createBrandForm.brand_type),
      name: 'is_fcb'
    },
    {
      label: 'Foodcourt',
      checked: FC_BRAND_TYPE_OPTIONS.some((opt) => opt.value === createBrandForm.brand_type),
      name: 'foodcourt'
    }
  ];

  return (
    <div>
      <div className='w-full max-w-[746px]'>
        <BrandDetailsCard
          data={{ label: 'Brand Name', value: createBrandForm?.name }}
          className='my-6'
        />
        <BrandDetailsCard
          data={{ label: 'Brand Slug', value: createBrandForm?.slug }}
          className='my-6'
        />
        <BrandDetailsCard
          data={{ label: 'Brand Tagline', value: createBrandForm?.short_text }}
          className='my-6'
        />
        <BrandDetailsCard
          data={{ label: 'Brand Summary', value: createBrandForm?.summary }}
          className='my-6'
        />
        <BrandDetailsCard
          data={{ label: 'Brand Description', value: createBrandForm?.description }}
          className='my-6'
        />
        <fieldset className='flex gap-4'>
          {brandTypes.map((item: BrandTypeCheckbox) => (
            <label key={item.name} className='flex items-center gap-2 mt-3 ml-2'>
              <Input
                type='radio'
                name='brandType'
                value={item.name}
                checked={item.checked}
                disabled
                className='form-radio'
              />
              {item.label}
            </label>
          ))}
        </fieldset>

        {createBrandForm.preorder && (
          <BrandDetailsCard
            data={{
              label: 'Pre order duration in hours',
              value: createBrandForm?.preorder_duration_in_hours
            }}
            className='my-6'
          />
        )}

        {createBrandForm.brand_type ? (
          <BrandDetailsCard
            data={{
              label: 'Brand Type',
              value: ALL_BRAND_TYPE_OPTIONS.find((o) => o.value === createBrandForm.brand_type)
                ?.text
            }}
            className='my-6'
          />
        ) : null}

        {createBrandForm.cutoff_time ? (
          <BrandDetailsCard
            data={{
              label: 'Cutoff Time',
              value: formatTimeSelector(createBrandForm.cutoff_time)
            }}
            className='my-6'
          />
        ) : null}

        {isFCBBrand(createBrandForm.brand_type) ? (
          <BrandDetailsCard
            data={{
              label: 'Companies',
              value: createBrandForm.all_company_available
                ? 'All companies'
                : selectedCompanies.join(', ')
            }}
            className='my-6'
          />
        ) : null}

        <BrandStatus
          isNew={createBrandForm?.new}
          isTop={createBrandForm?.top}
          popup={createBrandForm?.pop_up}
          comingSoon={createBrandForm?.coming_soon}
          setComingSoon={(k: boolean) => setCreateBrandForm({ ...createBrandForm, coming_soon: k })}
          setNew={(k: boolean) => setCreateBrandForm({ ...createBrandForm, new: k })}
          setPopup={(k: boolean) => setCreateBrandForm({ ...createBrandForm, pop_up: k })}
          comment={createBrandForm?.coming_soon_text}
          setComment={(k: string) =>
            setCreateBrandForm({ ...createBrandForm, coming_soon_text: k })
          }
          color={createBrandForm?.popup_color}
          setColor={(k: string) => setCreateBrandForm({ ...createBrandForm, popup_color: k })}
          setIsTop={(k: boolean) => setCreateBrandForm({ ...createBrandForm, top: k })}
          className='my-6'
          disabled
        />

        {!isFCBBrand(createBrandForm.brand_type) && (
          <div>
            {createBrandForm?.brand_working_hours?.map(
              (item: WorkingHoursFormApiType, index: number) => {
                const openingTime = formatTimeSelector(item.opening_time);
                const closingTime = formatTimeSelector(item.closing_time);
                return (
                  item.active && (
                    <BrandDetailsCard
                      key={index}
                      data={{
                        label: item.name,
                        value: `${openingTime} - ${closingTime}`
                      }}
                      className='my-6'
                    />
                  )
                );
              }
            )}
          </div>
        )}

        <BrandImageDetailCard data={brandImageData} />

        <div>
          <p className='text-[#858585] text-sm font-medium mt-6'>Available Polygons</p>

          {createBrandForm?.polygon?.map(
            (polygon, index) =>
              polygon.available && (
                <div key={index} className='relative  pr-5'>
                  <HexagonIcon
                    className=' absolute top-3 left-3'
                    style={{ color: POLYGON_COLORS[index]?.color_code }}
                  />
                  <BrandDetailsCard
                    data={{
                      label: polygon.name,
                      value: ''
                    }}
                    className='pl-14 my-6'
                  />
                </div>
              )
          )}
        </div>

        <div className='w-full flex gap-[30px] border border-[#EAEBEB] px-[24px] py-[20px] rounded-xl my-6  justify-start'>
          <div className='w-[150px]'>
            <p className='text-[#858585] text-sm font-medium'>Keywords</p>
          </div>

          <div className='flex gap-[12px] flex-wrap'>
            {createBrandForm?.brand_keywords?.map((item, index) => (
              <div
                key={index}
                className='bg-[#FEF0F2] flex gap-[5px] items-center rounded-[20px] px-[12px] pt-[2px] pb-[3px]'>
                <p className='text-[#E60A2B] text-sm'>{item.name}</p>
              </div>
            ))}
          </div>
        </div>
        <div className='w-full flex gap-[30px] border border-[#EAEBEB] px-[24px] py-[20px] rounded-xl my-6  justify-start'>
          <div className='w-[150px]'>
            <p className='text-[#858585] text-sm font-medium'>Descriptive Metadata</p>
          </div>

          <div className='flex gap-[12px] flex-wrap'>
            {createBrandForm?.brand_descriptive_metadatas?.map((item, index) => (
              <div
                key={index}
                className='bg-[#FEF0F2] flex gap-[5px] items-center rounded-[20px] px-[12px] pt-[2px] pb-[3px]'>
                <p className='text-[#E60A2B] text-sm'>{item.name}</p>
              </div>
            ))}
          </div>
        </div>
        <div className='w-full flex gap-[30px] border border-[#EAEBEB] px-[24px] py-[20px] rounded-xl my-6  justify-start'>
          <div className='w-[150px]'>
            <p className='text-[#858585] text-sm font-medium'>Business Metadata</p>
          </div>

          <div className='flex gap-[12px] flex-wrap'>
            {createBrandForm?.brand_business_metadatas?.map((item, index) => (
              <div
                key={index}
                className='bg-[#FEF0F2] flex gap-[5px] items-center rounded-[20px] px-[12px] pt-[2px] pb-[3px]'>
                <p className='text-[#E60A2B] text-sm'>{item.name}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateBrandPreview;
